import { useMemo } from 'react';

import _ from '@luna/lodash';
import { t } from '@luna/i18n';
import type { ProductWithHelpers } from '@shared/products-core';
import { getProductType } from '@shared/products-core';
import { getUnderlyingProductId } from '@retail/overview-products-utils';

import type { MultiProfile, MultiProfileProduct, MultiProfileType } from './useMultiProfiles';
import { useMultiProfiles } from './useMultiProfiles';

export interface MultiProfileSection {
    id: string;
    title: string;
    subTitle?: string;
    type?: MultiProfileType;
    hasMissingProducts: boolean;
    products: ProductWithHelpers[];
}

function getLocalProductId(product: ProductWithHelpers): string {
    const underlyingProductId = getUnderlyingProductId(product);
    return underlyingProductId ?? product.productId ?? product.id;
}

function findProfileForProduct(
    profiles: MultiProfile[],
    product: ProductWithHelpers,
): MultiProfile | undefined {
    const localProductId = getLocalProductId(product);
    return profiles.find(({ products }) => products.some(({ id }) => id === localProductId));
}

function getUnassignedProducts(
    products: ProductWithHelpers[],
    profiles: MultiProfile[],
): ProductWithHelpers[] {
    return products.filter((product) => !findProfileForProduct(profiles, product));
}

function enrichWithVirtualProfiles(
    products: ProductWithHelpers[],
    profiles: MultiProfile[],
): MultiProfile[] {
    if (profiles.length <= 1) {
        return profiles;
    }

    const unassignedProducts = getUnassignedProducts(products, profiles);

    if (!unassignedProducts.length) {
        return profiles;
    }

    return [
        ...profiles,
        {
            id: 'OTHER_BANKS_PROFILE',
            name: t('main.menu.v2.profiles.name.otherBanks'),
            entityType: 'OTHER_BANKS',
            products: unassignedProducts.map((product) => ({
                id: getLocalProductId(product),
                type: getProductType(product),
            })),
        },
    ];
}

function getProfileTypeTranslation(profileType?: MultiProfileType): string | undefined {
    const translationMap: { [key in MultiProfileType]?: string } = {
        PHYSICAL_PERSON: 'main.menu.v2.profiles.type.physical',
        LEGAL_ENTITY: 'main.menu.v2.profiles.type.legal',
    };

    return profileType ? translationMap[profileType] : undefined;
}

function getProfileSubtitle(
    entityType?: MultiProfileType,
    entityRegistrationNumber?: string,
): string | undefined {
    if (entityRegistrationNumber) {
        return t('main.menu.v2.profiles.registrationNumber', {
            number: entityRegistrationNumber,
        });
    }

    const profileTypeTranslation = getProfileTypeTranslation(entityType);

    if (profileTypeTranslation) {
        return t(profileTypeTranslation);
    }

    return undefined;
}

function multiProfileProductToProductWithHelpers(
    multiProfileProduct: MultiProfileProduct,
    allProducts: ProductWithHelpers[],
): ProductWithHelpers | undefined {
    return allProducts.find((product) => {
        const localProductId = getLocalProductId(product);
        return localProductId === multiProfileProduct.id;
    });
}

function multiProfileToSection(
    { id, name, entityType, entityRegistrationNumber, products }: MultiProfile,
    allProducts: ProductWithHelpers[],
): MultiProfileSection {
    const profileProducts = products.map((product) =>
        multiProfileProductToProductWithHelpers(product, allProducts),
    );
    const [matchedProducts, unmatchedProducts] = _.partition(
        profileProducts,
        (product) => !!product,
    );

    return {
        id,
        title: name,
        subTitle: getProfileSubtitle(entityType, entityRegistrationNumber),
        type: entityType,
        hasMissingProducts: !!unmatchedProducts.length,
        products: matchedProducts as ProductWithHelpers[],
    };
}

export function useMultiProfileSections(products: ProductWithHelpers[]): MultiProfileSection[] {
    const { multiProfiles } = useMultiProfiles();

    return useMemo(
        () =>
            enrichWithVirtualProfiles(products, multiProfiles).map((profile) =>
                multiProfileToSection(profile, products),
            ),
        [multiProfiles, products],
    );
}
